import { Typography } from '../primitives/index.js'
import { TreeItem, type TreeItemProps } from '../primitives/tree-view.js'
import { Page } from '@tunasong/icons'
import type { ColorName, Entity, Persisted } from '@tunasong/schemas'
import type { FC, Ref } from 'react'
import { getIconColorCSS, hashColorName } from '../color/index.js'

export interface EntityTreeItemProps extends Omit<TreeItemProps, 'itemId'> {
  color?: ColorName
  Icon?: FC
  entity: Persisted<Entity>
  open?: boolean
  /** Item is excluded from selection */
  excluded?: boolean
  /** Listed but can't be chosen, and says why, e.g. "You can't add items here" (GH #2450) */
  disabledReason?: string
  ref?: Ref<unknown>
}

export const EntityTreeItem = (props: EntityTreeItemProps) => {
  const {
    entity,
    ref,
    Icon = Page,
    color = entity?.type ? hashColorName(entity?.type) : 'grey',
    excluded = false,
    disabledReason,
    ...other
  } = props

  if (!entity) {
    return null
  }

  /** @todo factor out getEntityLabel, etc.  */
  const labelText = entity?.name

  return (
    <TreeItem
      itemId={entity.id}
      label={
        <div
          ref={ref as React.Ref<HTMLDivElement>}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '4px 0',
            opacity: excluded ? 0.5 : 1,
          }}
        >
          {/* A disabled row dims its icon and name only; the reason stays readable (Joni, #2514) */}
          <Icon style={{ color: getIconColorCSS(color), padding: '0 4px 0 0', opacity: disabledReason ? 0.5 : 1 }} />
          <Typography variant="body2" style={{ opacity: disabledReason ? 0.5 : 1 }}>
            {labelText}
          </Typography>
          {disabledReason ? (
            <Typography
              variant="caption"
              style={{ marginLeft: 'var(--tuna-spacing-1)', color: 'var(--tuna-content-secondary)' }}
            >
              {disabledReason}
            </Typography>
          ) : null}
        </div>
      }
      aria-disabled={disabledReason ? 'true' : undefined}
      {...other}
    />
  )
}
